import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiTrendingUp, FiUsers, FiAward, FiCheckCircle, FiArrowRight, FiStar, FiClock, FiTarget } = FiIcons;

const HomePage = () => {
  const stats = [
    { number: '500+', label: 'Companies Secured' },
    { number: '99.9%', label: 'Compliance Rate' },
    { number: '24/7', label: 'Support Available' },
    { number: '50%', label: 'Faster Implementation' }
  ];

  const benefits = [
    { icon: FiShield, title: 'Enterprise Security', description: 'Bank-level security for your sensitive data' },
    { icon: FiTrendingUp, title: 'ROI Guaranteed', description: 'See measurable results within 30 days' },
    { icon: FiUsers, title: 'Expert Team', description: 'Certified Microsoft Purview specialists' },
    { icon: FiAward, title: 'Proven Results', description: '98% client satisfaction rate' }
  ];

  const testimonials = [
    { name: 'Sarah Johnson', role: 'Chief Data Officer, TechCorp', content: 'YourPurviewPartner transformed our data governance in just 6 weeks. We went from chaos to complete compliance.', rating: 5 },
    { name: 'Michael Chen', role: 'IT Director, GlobalFinance', content: 'The ROI was immediate. We reduced compliance costs by 60% while improving data security.', rating: 5 },
    { name: 'Lisa Rodriguez', role: 'VP Operations, HealthTech Inc', content: 'Outstanding support and expertise. They made Microsoft Purview work seamlessly with our existing systems.', rating: 5 }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Russell Brunson's Hook-Story-Offer Formula */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Hook */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Stop Losing <span className="text-yellow-400">$2.3M Per Year</span><br />
                Due to Data Compliance Failures
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Fortune 500 companies trust us to implement Microsoft Purview and achieve <span className="text-yellow-400 font-semibold">100% compliance in 190 days</span>
              </p>
            </motion.div>

            {/* Urgency & Social Proof */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-red-600 text-white px-6 py-3 rounded-lg inline-block mb-8"
            >
              <SafeIcon icon={FiClock} className="inline mr-2" />
              <strong>Limited Time:</strong> Purview Data Governance Audit (Worth $5,000) - Only 10 Spots Left This Month
            </motion.div>

            {/* Primary CTA */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-4"
            >
              <Link 
                to="/purview-audit" 
                className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105 shadow-2xl"
              >
                Get Your Purview $5,000 Audit Now
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
              <p className="text-sm text-blue-200">No obligations • Results in 24 hours • 100% confidential</p>
            </motion.div>

            {/* Stats Row */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-400">{stat.number}</div>
                  <div className="text-blue-100">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Agitation Section */}
      <section className="py-16 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-red-800 mb-6">
              The Hidden Cost of Poor Data Governance
            </h2>
            <p className="text-xl text-red-700 max-w-3xl mx-auto">
              Every day without proper Microsoft Purview implementation, your company bleeds money...
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-red-500">
              <h3 className="text-2xl font-bold text-red-800 mb-4">Compliance Fines</h3>
              <p className="text-red-700 text-lg mb-4">Average GDPR fine: $2.3M per violation</p>
              <p className="text-gray-600">Companies without proper data governance face 300% higher risk of regulatory penalties</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-red-500">
              <h3 className="text-2xl font-bold text-red-800 mb-4">Data Breaches</h3>
              <p className="text-red-700 text-lg mb-4">Average cost: $4.45M per breach</p>
              <p className="text-gray-600">83% of breaches could be prevented with proper data classification and protection</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-red-500">
              <h3 className="text-2xl font-bold text-red-800 mb-4">Lost Productivity</h3>
              <p className="text-red-700 text-lg mb-4">40 hours/week wasted on manual processes</p>
              <p className="text-gray-600">Teams spend 60% of their time searching for data instead of analyzing it</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              The YourPurviewPartner Advantage: Your Path to Data Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven 4-step methodology has helped 500+ companies achieve complete data governance in record time
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 bg-blue-50 rounded-lg hover:shadow-lg transition-shadow"
              >
                <SafeIcon icon={benefit.icon} className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Ladder Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Choose Your Success Path</h2>
            <p className="text-xl text-blue-100">From quick wins to complete transformation - we have the perfect solution for your needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Starter Package */}
            <div className="bg-white text-gray-900 p-8 rounded-lg shadow-xl">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">Quick Start</h3>
                <div className="text-4xl font-bold text-blue-600 mb-2">$2,997</div>
                <p className="text-gray-600">Perfect for small teams</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Basic Purview Setup
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Data Classification
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  30-day Support
                </li>
              </ul>
              <Link to="/contact" className="block w-full text-center bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Get Started
              </Link>
            </div>

            {/* Professional Package - Most Popular */}
            <div className="bg-white text-gray-900 p-8 rounded-lg shadow-xl border-4 border-orange-400 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-400 text-white px-6 py-2 rounded-full font-bold">
                MOST POPULAR
              </div>
              <div className="text-center mb-6 mt-4">
                <h3 className="text-2xl font-bold mb-2">Professional</h3>
                <div className="text-4xl font-bold text-blue-600 mb-2">$9,997</div>
                <p className="text-gray-600">For growing companies</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Complete Implementation
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Staff Training
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Compliance Reporting
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  90-day Support
                </li>
              </ul>
              <Link to="/contact" className="block w-full text-center bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
                Choose Professional
              </Link>
            </div>

            {/* Enterprise Package */}
            <div className="bg-white text-gray-900 p-8 rounded-lg shadow-xl">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
                <div className="text-4xl font-bold text-blue-600 mb-2">Custom</div>
                <p className="text-gray-600">For large organizations</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Custom Integration
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Dedicated Team
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  24/7 Support
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                  Ongoing Optimization
                </li>
              </ul>
              <Link to="/contact" className="block w-full text-center bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Don't Just Take Our Word For It...</h2>
            <p className="text-xl text-gray-600">See what our clients are saying about their transformation</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 h-5 w-5" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA with Scarcity */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Stop Losing Money on Data Compliance?</h2>
          <p className="text-xl mb-8">Join the 500+ companies who've transformed their data governance with YourPurviewPartner</p>
          <div className="bg-white text-red-600 p-6 rounded-lg mb-8 inline-block">
            <SafeIcon icon={FiTarget} className="h-8 w-8 mx-auto mb-2" />
            <div className="font-bold text-lg">WARNING: Only 7 Purview Audits Left This Month</div>
            <div className="text-sm">Don't let your competitors get ahead while you stay vulnerable</div>
          </div>
          <Link 
            to="/purview-audit" 
            className="inline-flex items-center bg-white text-orange-600 px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl"
          >
            Claim Your Purview $5,000 Audit Now
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </Link>
          <p className="text-sm mt-4 opacity-90">✓ No risk ✓ No obligations ✓ Results in 24 hours ✓ 100% confidential</p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;